import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { Grid, Stack, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProcessStep } from '../common/enums/enums';
import GenButton from './GenButton';
import { GenCurrency } from './GenDataFormat';
import PercentageGauge from './PercentageGauge';

const Wip = ({ value }) => {
  const { t } = useTranslation('stores');

  return (
    <Stack direction="column" alignItems="flex-start" sx={{ minWidth: '90px' }}>
      <Typography variant="caption" color="text.secondary">
        {t('card.columns.wip')}
      </Typography>
      <Typography variant="subtitle2">
        <GenCurrency value={value} />
      </Typography>
    </Stack>
  );
};

const Revenue = ({ value }) => {
  const { t } = useTranslation('stores');
  return (
    <Stack direction="column" alignItems="flex-start" sx={{ minWidth: '90px' }}>
      <Typography variant="caption" color="text.secondary">
        {t('card.columns.revenue')}
      </Typography>
      <Typography variant="subtitle2">
        {value ? <GenCurrency value={value} /> : '-'}
      </Typography>
    </Stack>
  );
};

const QueuesList = ({ data, title }) => {
  const { t } = useTranslation('stores');
  return (
    <Stack
      direction="column"
      alignItems="flex-start"
      sx={{ minWidth: '120px' }}
    >
      <Typography variant="caption" color="text.secondary" noWrap>
        {title}
      </Typography>
      <Typography variant="subtitle2">
        {`${data['In Transit'] ? data['In Transit'] : 0} ${
          ProcessStep.IN_TRANSIT
        }`}
      </Typography>
      <Typography variant="subtitle2">
        {`${data['Arrived'] ? data['Arrived'] : 0} ${ProcessStep.ARRIVED}`}
      </Typography>
      <Typography variant="subtitle2">
        {`${data['In Process'] ? data['In Process'] : 0} ${
          ProcessStep.IN_PROCESS
        }`}
      </Typography>
      <Typography variant="subtitle2">
        {`${data['Done'] ? data['Done'] : 0} ${t('card.rows.completed')}`}
      </Typography>
      <Typography variant="subtitle2" color={data['Late'] ? 'error' : null}>
        {`${data['Late'] ? data['Late'] : 0} > ${t('card.rows.compliance')}`}
      </Typography>
    </Stack>
  );
};

const ExceedsExpectations = ({ value }) => {
  const { t } = useTranslation('stores');
  return (
    <Stack direction="column" alignItems="flex-start">
      <Typography variant="caption" color="text.secondary">
        {t('card.columns.exceed')}
      </Typography>
      <Typography variant="subtitle2">
        {value ? `${Math.round(value * 100)}%` : '-'}
      </Typography>
    </Stack>
  );
};

const DataQuality = ({ value }) => {
  const { t } = useTranslation('stores');
  const rules = {
    success: {
      top: 100,
      bottom: 98,
    },
    warning: {
      top: 97,
      bottom: 95,
    },
  };
  return (
    <Stack
      direction="column"
      alignItems={{ xs: 'flex-start', md: 'center' }}
      gap={1}
    >
      <Typography variant="caption" color="text.secondary">
        {t('card.columns.dQuality')}
      </Typography>
      <PercentageGauge percentageValue={value} rules={rules} />
    </Stack>
  );
};

const DeliveryOnTime = ({ value }) => {
  const { t } = useTranslation('stores');
  return (
    <Stack
      direction="column"
      alignItems={{ xs: 'flex-start', md: 'center' }}
      gap={1}
      sx={{ minWidth: '120px' }}
    >
      <Typography variant="caption" color="text.secondary">
        {t('card.columns.dTime')}
      </Typography>
      <PercentageGauge percentageValue={value} />
    </Stack>
  );
};

const StoreCard = ({ data, rowClick }) => {
  const { t } = useTranslation('stores');
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState(false);

  const handleCollapse = e => {
    e.stopPropagation();
    setCollapsed(state => !state);
  };

  return (
    <Stack
      direction="column"
      sx={{
        border: 1,
        borderColor: theme.palette.divider,
        background: theme.palette.background.paper,
        borderRadius: 1,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => rowClick(data.vendorId)}
        sx={{
          cursor: 'pointer',
        }}
        py={1}
        px={2}
      >
        <Typography variant="subtitle2">{`${data?.storeNumber} - ${data?.vendorName}`}</Typography>
        <GenButton
          onClick={e => handleCollapse(e)}
          iconForIconButton={
            collapsed ? <KeyboardArrowDown /> : <KeyboardArrowUp />
          }
          sx={{
            color: theme.palette.grey[500],
          }}
        />
      </Stack>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        py={1}
        px={2}
        sx={{
          display: `${collapsed ? 'none' : 'flex'}`,
          borderTop: 1,
          borderColor: theme.palette.divider,
        }}
      >
        <Grid item xs={12} sm={9.5} md={8.6}>
          <Grid
            container
            direction="row"
            justifyContent="space-between"
            rowSpacing={{ xs: 2, md: 0 }}
            mb={{ xs: 1, sm: 0 }}
          >
            <Grid item xs={6} md={2.4}>
              <Wip value={data?.wip} />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <Revenue value={data?.revenue} />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <QueuesList
                data={data?.detailQueue}
                title={t('card.columns.detailsQ')}
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <QueuesList
                data={data?.deliveryQueue}
                title={t('card.columns.deliverQ')}
              />
            </Grid>
            <Grid item xs={6} md={2.4}>
              <ExceedsExpectations value={data?.exceedsExpectations} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={2.5} md={3.4}>
          <Grid
            container
            direction={{ xs: 'row', sm: 'column', md: 'row' }}
            justifyContent={{ xs: 'flex-start' }}
            rowSpacing={{ xs: 2, md: 0 }}
          >
            <Grid item xs={6}>
              <DataQuality value={data?.dataQuality} />
            </Grid>
            <Grid item xs={6}>
              <DeliveryOnTime value={data?.deliveryOnTime} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default StoreCard;
